/**
 * AI Insights & Risk Analysis Component for Profile360
 * Comprehensive security and access insights dashboard
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import {
  Close as CloseIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import StaleAppsComponent from './StaleAppsComponent';

// Styled components
const InsightsCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
  },
}));

const RiskScoreContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 80,
  height: 80,
  borderRadius: '50%',
  border: '4px solid',
  borderColor: theme.palette.error.main,
  margin: theme.spacing(2, 'auto'),
}));

const RiskScore = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 'bold',
  color: theme.palette.error.main,
}));



const ImpactBar = styled(Box)(({ theme, level }) => ({
  width: '100%',
  height: 8,
  borderRadius: 4,
  backgroundColor: level === 'high' ? '#e74c3c' : 
                   level === 'medium' ? '#f39c12' : '#27ae60',
  marginTop: theme.spacing(1),
}));

/**
 * AI Insights Component
 */
const AIInsightsComponent = ({ userEmail, isOpen, onClose }) => {
  const [insights, setInsights] = useState({
    riskScore: 87,
    totalInsights: 9,
    highRisk: 4,
    mediumRisk: 4,
    lowRisk: 1,
    lastUpdated: new Date().toLocaleDateString()
  });
  const [insightDetails, setInsightDetails] = useState([]);

  const [showStaleAppsDetail, setShowStaleAppsDetail] = useState(false);

  /**
   * Handle data from child components
   */
  const handleInsightData = (data) => {
    setInsightDetails(prev => {
      const filtered = prev.filter(item => item.type !== data.type);
      return [...filtered, data];
    });
  };

  /**
   * Calculate overall insights statistics
   */
  useEffect(() => {
    const stats = {
      riskScore: 87, // Base score
      totalInsights: insightDetails.length,
      highRisk: 0,
      mediumRisk: 0,
      lowRisk: 0,
      lastUpdated: new Date().toLocaleDateString()
    };

    insightDetails.forEach(insight => {
      if (insight.riskLevel === 'high') stats.highRisk++;
      else if (insight.riskLevel === 'medium') stats.mediumRisk++;
      else stats.lowRisk++;
    });

    // Adjust risk score based on insights
    if (stats.highRisk > 0) stats.riskScore = Math.max(70, stats.riskScore - (stats.highRisk * 10));
    if (stats.mediumRisk > 0) stats.riskScore = Math.max(80, stats.riskScore - (stats.mediumRisk * 5));

    setInsights(stats);
  }, [insightDetails]);







  if (!isOpen) return null;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '70vh'
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5" fontWeight="bold">
              AI Insights & Risk Analysis
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Comprehensive security and access insights
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            <RiskScoreContainer>
              <RiskScore>{insights.riskScore}</RiskScore>
            </RiskScoreContainer>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {showStaleAppsDetail ? (
          // Detailed Stale Apps View
          <Box>
            <Box display="flex" alignItems="center" mb={2}>
              <Button
                onClick={() => setShowStaleAppsDetail(false)}
                sx={{ mr: 2 }}
              >
                ← Back
              </Button>
              <Typography variant="h6">Stale Access Details</Typography>
            </Box>
            <StaleAppsComponent
              userEmail={userEmail}
              onDataLoad={handleInsightData}
            />
          </Box>
        ) : (
          // Main Insights Overview
          <Box>
            {/* Top Stats Row */}
            <Grid container spacing={2} mb={3}>
              <Grid item xs={3}>
                <Card sx={{ textAlign: 'center', p: 2 }}>
                  <Typography variant="h3" color="error.main" fontWeight="bold">
                    {insights.highRisk}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    High Risk
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={3}>
                <Card sx={{ textAlign: 'center', p: 2 }}>
                  <Typography variant="h3" color="warning.main" fontWeight="bold">
                    {insights.mediumRisk}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Medium Risk
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={3}>
                <Card sx={{ textAlign: 'center', p: 2 }}>
                  <Typography variant="h3" color="success.main" fontWeight="bold">
                    {insights.lowRisk}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Low Risk
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={3}>
                <Card sx={{ textAlign: 'center', p: 2 }}>
                  <Typography variant="h3" color="primary.main" fontWeight="bold">
                    {insights.totalInsights}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Insights
                  </Typography>
                </Card>
              </Grid>
            </Grid>

            {/* Insights Cards Grid */}
            <Grid container spacing={2}>
              {/* Row 1 */}
              <Grid item xs={4}>
                <InsightsCard sx={{ p: 2, minHeight: 140 }}>
                  <Typography variant="h6" gutterBottom>Dormant Account</Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Account inactivity &gt; 90 days
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Impact Level
                  </Typography>
                  <ImpactBar level="medium" />
                </InsightsCard>
              </Grid>
              <Grid item xs={4}>
                <InsightsCard sx={{ p: 2, minHeight: 140 }}>
                  <Typography variant="h6" gutterBottom>Privilege Creep</Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    More roles than needed
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Impact Level
                  </Typography>
                  <ImpactBar level="high" />
                </InsightsCard>
              </Grid>
              <Grid item xs={4}>
                <InsightsCard sx={{ p: 2, minHeight: 140 }}>
                  <Typography variant="h6" gutterBottom>Suspicious Activity</Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Access from 2 regions
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Impact Level
                  </Typography>
                  <ImpactBar level="high" />
                </InsightsCard>
              </Grid>

              {/* Row 2 */}
              <Grid item xs={4}>
                <InsightsCard sx={{ p: 2, minHeight: 140 }}>
                  <Typography variant="h6" gutterBottom>Behavioral Anomalies</Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Login at 3AM
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Impact Level
                  </Typography>
                  <ImpactBar level="high" />
                </InsightsCard>
              </Grid>
              <Grid item xs={4}>
                <InsightsCard sx={{ p: 2, minHeight: 140 }}>
                  <Typography variant="h6" gutterBottom>Excessive Access</Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Access to 14 systems
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Impact Level
                  </Typography>
                  <ImpactBar level="medium" />
                </InsightsCard>
              </Grid>
              <Grid item xs={4}>
                <InsightsCard
                  sx={{ p: 2, minHeight: 140, cursor: 'pointer' }}
                  onClick={() => setShowStaleAppsDetail(true)}
                >
                  <Typography variant="h6" gutterBottom>Stale Access</Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    No stale access
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Impact Level
                  </Typography>
                  <ImpactBar level="low" />
                </InsightsCard>
              </Grid>
            </Grid>

            {/* Summary */}
            <Box mt={3}>
              <Typography variant="body2" color="text.secondary">
                Last updated: {insights.lastUpdated}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AIInsightsComponent;
